import React from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Space, 
  Typography, 
  Table, 
  Statistic, 
  Input,
  Select,
  Tabs,
  Tag,
  Modal,
  Form,
  message 
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  FilterOutlined,
  DashboardOutlined,
  MobileOutlined,
  DesktopOutlined
} from '@ant-design/icons';
import { 
  useIsMobile, 
  getMobileTableProps, 
  getMobileButtonProps,
  getSpaceDirection,
  getResponsiveColumns,
  getMobileInputProps,
  getMobileCardProps
} from '../../utils/responsiveUtils';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export const MobileResponsiveExample: React.FC = () => {
  const isMobile = useIsMobile();
  const [modalVisible, setModalVisible] = React.useState(false);
  const [form] = Form.useForm();

  // Sample data for demonstration
  const mockData = [
    { id: '1', name: 'Product A', category: 'Electronics', stock: 150, value: 25000, status: 'In Stock' },
    { id: '2', name: 'Product B', category: 'Clothing', stock: 25, value: 5000, status: 'Low Stock' },
    { id: '3', name: 'Product C', category: 'Books', stock: 0, value: 0, status: 'Out of Stock' },
    { id: '4', name: 'Product D', category: 'Electronics', stock: 85, value: 15000, status: 'In Stock' },
  ];

  // Mobile-optimized table columns
  const columns = [
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
      width: isMobile ? '40%' : '25%',
      render: (name: string) => <Text strong>{name}</Text>
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      responsive: ['md'], // Hide on mobile
      width: '20%',
    },
    {
      title: 'Stock',
      dataIndex: 'stock',
      key: 'stock',
      width: isMobile ? '30%' : '15%',
      render: (stock: number) => (
        <Tag color={stock > 50 ? 'green' : stock > 0 ? 'orange' : 'red'}>
          {stock}
        </Tag>
      )
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      responsive: ['sm'], // Hide on extra small screens
      width: '20%',
      render: (value: number) => `$${value.toLocaleString()}`
    },
    {
      title: 'Actions',
      key: 'actions',
      width: isMobile ? '30%' : '20%',
      render: () => (
        <Space size="small" direction={isMobile ? 'vertical' : 'horizontal'}>
          <Button 
            type="link" 
            size="small"
            {...getMobileButtonProps(isMobile)}
          >
            Edit
          </Button>
          <Button 
            type="link" 
            size="small" 
            danger
            {...getMobileButtonProps(isMobile)}
          >
            Delete
          </Button>
        </Space>
      )
    }
  ];

  const handleAddProduct = () => {
    setModalVisible(true);
  };

  const handleSubmit = (values: any) => {
    console.log('Form values:', values);
    message.success('Product added successfully!');
    setModalVisible(false);
    form.resetFields();
  };

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Space 
          direction={getSpaceDirection(isMobile)}
          style={{ width: '100%', justifyContent: 'space-between' }}
        >
          <div>
            <Title level={isMobile ? 3 : 2}>
              <MobileOutlined style={{ marginRight: 8 }} />
              Mobile Responsive Demo
            </Title>
            <Text type="secondary">
              {isMobile ? 'Mobile View' : 'Desktop View'} - 
              Resize your browser to see responsiveness
            </Text>
          </div>
          <Space direction={getSpaceDirection(isMobile)}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAddProduct}
              {...getMobileButtonProps(isMobile)}
            >
              {isMobile ? 'Add' : 'Add Product'}
            </Button>
            <Button 
              icon={<FilterOutlined />}
              {...getMobileButtonProps(isMobile)}
            >
              Filter
            </Button>
          </Space>
        </Space>
      </div>

      {/* Statistics Cards - Responsive Grid */}
      <Row gutter={[8, 8]} style={{ marginBottom: 24 }}>
        <Col {...getResponsiveColumns()}>
          <Card {...getMobileCardProps(isMobile)}>
            <Statistic
              title="Total Products"
              value={mockData.length}
              prefix={<DashboardOutlined />}
              valueStyle={{ 
                fontSize: isMobile ? '18px' : '24px',
                textAlign: isMobile ? 'center' : 'left'
              }}
            />
          </Card>
        </Col>
        <Col {...getResponsiveColumns()}>
          <Card {...getMobileCardProps(isMobile)}>
            <Statistic
              title="Total Value"
              value={45000}
              prefix="$"
              valueStyle={{ 
                fontSize: isMobile ? '18px' : '24px',
                color: '#3f8600',
                textAlign: isMobile ? 'center' : 'left'
              }}
            />
          </Card>
        </Col>
        <Col {...getResponsiveColumns()}>
          <Card {...getMobileCardProps(isMobile)}>
            <Statistic
              title="Low Stock"
              value={1}
              valueStyle={{ 
                fontSize: isMobile ? '18px' : '24px',
                color: '#fa8c16',
                textAlign: isMobile ? 'center' : 'left'
              }}
            />
          </Card>
        </Col>
        <Col {...getResponsiveColumns()}>
          <Card {...getMobileCardProps(isMobile)}>
            <Statistic
              title="Out of Stock"
              value={1}
              valueStyle={{ 
                fontSize: isMobile ? '18px' : '24px',
                color: '#ff4d4f',
                textAlign: isMobile ? 'center' : 'left'
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Search and Filters */}
      <Card {...getMobileCardProps(isMobile)} style={{ marginBottom: 16 }}>
        <Space 
          direction={getSpaceDirection(isMobile)}
          style={{ width: '100%' }}
          size={isMobile ? 'small' : 'middle'}
        >
          <Input
            placeholder="Search products..."
            prefix={<SearchOutlined />}
            {...getMobileInputProps(isMobile)}
            style={{ width: isMobile ? '100%' : 200 }}
          />
          <Select
            placeholder="Category"
            {...getMobileInputProps(isMobile)}
            style={{ width: isMobile ? '100%' : 150 }}
          >
            <Select.Option value="electronics">Electronics</Select.Option>
            <Select.Option value="clothing">Clothing</Select.Option>
            <Select.Option value="books">Books</Select.Option>
          </Select>
          <Select
            placeholder="Status"
            {...getMobileInputProps(isMobile)}
            style={{ width: isMobile ? '100%' : 150 }}
          >
            <Select.Option value="in_stock">In Stock</Select.Option>
            <Select.Option value="low_stock">Low Stock</Select.Option>
            <Select.Option value="out_of_stock">Out of Stock</Select.Option>
          </Select>
        </Space>
      </Card>

      {/* Tabs with Responsive Content */}
      <Tabs 
        defaultActiveKey="table" 
        size={isMobile ? 'small' : 'large'}
      >
        <TabPane tab="Table View" key="table">
          <Card {...getMobileCardProps(isMobile)}>
            <Table
              columns={columns}
              dataSource={mockData}
              rowKey="id"
              {...getMobileTableProps(isMobile)}
            />
          </Card>
        </TabPane>
        
        <TabPane tab="Grid View" key="grid">
          <Row gutter={[8, 8]}>
            {mockData.map((item) => (
              <Col 
                key={item.id}
                xs={24} 
                sm={12} 
                md={8} 
                lg={6}
              >
                <Card 
                  {...getMobileCardProps(isMobile)}
                  title={item.name}
                  extra={
                    <Tag color={item.stock > 50 ? 'green' : item.stock > 0 ? 'orange' : 'red'}>
                      {item.status}
                    </Tag>
                  }
                >
                  <p><strong>Category:</strong> {item.category}</p>
                  <p><strong>Stock:</strong> {item.stock}</p>
                  <p><strong>Value:</strong> ${item.value.toLocaleString()}</p>
                  <Space direction={getSpaceDirection(isMobile)}>
                    <Button 
                      type="primary" 
                      size="small"
                      {...getMobileButtonProps(isMobile)}
                    >
                      Edit
                    </Button>
                    <Button 
                      danger 
                      size="small"
                      {...getMobileButtonProps(isMobile)}
                    >
                      Delete
                    </Button>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </TabPane>
      </Tabs>

      {/* Mobile-Responsive Modal */}
      <Modal
        title="Add New Product"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '95vw' : 600}
        centered={!isMobile}
        style={isMobile ? { top: 20 } : {}}
      >
        <Form
          form={form}
          layout={isMobile ? 'vertical' : 'horizontal'}
          labelCol={isMobile ? undefined : { span: 6 }}
          wrapperCol={isMobile ? undefined : { span: 18 }}
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Product Name"
            name="name"
            rules={[{ required: true, message: 'Please enter product name' }]}
          >
            <Input {...getMobileInputProps(isMobile)} />
          </Form.Item>
          
          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: 'Please select category' }]}
          >
            <Select 
              placeholder="Select category"
              {...getMobileInputProps(isMobile)}
            >
              <Select.Option value="electronics">Electronics</Select.Option>
              <Select.Option value="clothing">Clothing</Select.Option>
              <Select.Option value="books">Books</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            label="Initial Stock"
            name="stock"
            rules={[{ required: true, message: 'Please enter stock quantity' }]}
          >
            <Input 
              type="number"
              {...getMobileInputProps(isMobile)}
            />
          </Form.Item>
          
          <Form.Item style={{ marginBottom: 0, textAlign: isMobile ? 'center' : 'right' }}>
            <Space direction={getSpaceDirection(isMobile)}>
              <Button 
                onClick={() => setModalVisible(false)}
                {...getMobileButtonProps(isMobile)}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                {...getMobileButtonProps(isMobile)}
              >
                Add Product
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Mobile Responsive Tips */}
      <Card 
        {...getMobileCardProps(isMobile)}
        title={
          <Space>
            {isMobile ? <MobileOutlined /> : <DesktopOutlined />}
            Mobile Responsiveness Tips
          </Space>
        }
        style={{ marginTop: 24 }}
      >
        <ul style={{ paddingLeft: isMobile ? 16 : 24 }}>
          <li>Tables use horizontal scroll on mobile with essential columns visible</li>
          <li>Button layouts stack vertically on mobile for better touch targets</li>
          <li>Input fields take full width on mobile for easier interaction</li>
          <li>Cards use smaller padding and margins on mobile to maximize space</li>
          <li>Statistics are center-aligned on mobile for better visual balance</li>
          <li>Modals are nearly full-screen on mobile for better usability</li>
          <li>Grid layouts automatically adjust based on screen size</li>
          <li>Typography scales appropriately for mobile readability</li>
        </ul>
      </Card>
    </div>
  );
}; 